import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'

import Loading from '../../components/loading'
import { ROUTES } from '../../constants/routes'
import useApi from '../../hooks/useApi'
import {
  CHARITY_LOCATION_TYPES,
  charityLocationPayload,
  charityLocationsEqual,
  parseGooglePlaceToCharityLocation,
} from '../../lib/charityLocation'
import {
  charityAccountPath,
  charityPublicProfilePath,
} from '../../lib/charityProfileLink'
import { ensureAbsoluteUrl } from '../../utils/url'
import CharitySettingsContent from './settings/CharitySettingsContent'
import { DONATION_LINK_FIELDS, SOCIAL_LINK_FIELDS } from './settings/constants'

function buildInitialLocations(charity) {
  return {
    volunteer: charity?.volunteer_location ?? null,
    donation: charity?.donation_location ?? null,
    visitor: charity?.visitor_location ?? null,
  }
}

function normalizeLinkField(field, raw) {
  if (field.includes('_url') || field.includes('_link')) {
    return ensureAbsoluteUrl(raw) || null
  }
  return raw || null
}

function fieldsChanged(charity, initial, fieldKeys) {
  if (!charity || !initial) return false
  return fieldKeys.some(
    key =>
      normalizeLinkField(key, charity[key]) !==
      normalizeLinkField(key, initial[key]),
  )
}

export default function CharityEdit() {
  const { t } = useTranslation()
  const { id: idParam } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const charityId = Number(idParam)

  const { getCharity, getUserCharityRegistrations, updateCharity } = useApi()

  const [charity, setCharity] = useState(null)
  const [initialCharity, setInitialCharity] = useState(null)
  const [locations, setLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [initialLocations, setInitialLocations] = useState({
    volunteer: null,
    donation: null,
    visitor: null,
  })
  const [clearedLocations, setClearedLocations] = useState({
    volunteer: false,
    donation: false,
    visitor: false,
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [accessDenied, setAccessDenied] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [stripeMessage, setStripeMessage] = useState(null)
  const [verifiedCharities, setVerifiedCharities] = useState([])

  const stripeReturnPath = charityAccountPath(charityId)

  const fetchCharity = useCallback(async () => {
    if (!charityId || Number.isNaN(charityId)) {
      setNotFound(true)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setAccessDenied(false)
    setNotFound(false)

    try {
      const [registrationsResponse, charityResponse] = await Promise.all([
        getUserCharityRegistrations(),
        getCharity(charityId),
      ])

      const registrations = registrationsResponse.data || []
      const verified = registrations.filter(
        reg => reg.verified && reg.status === 'Verified',
      )
      setVerifiedCharities(verified)

      const canEdit = verified.some(
        reg => Number(reg.charity?.id) === charityId,
      )
      if (!canEdit) {
        setAccessDenied(true)
        setLoading(false)
        return
      }

      if (charityResponse.status === 404 || charityResponse.error) {
        setNotFound(true)
        setLoading(false)
        return
      }

      const data = charityResponse.data
      if (!data) {
        setNotFound(true)
        setLoading(false)
        return
      }

      setCharity(data)
      setInitialCharity(data)
      const nextLocations = buildInitialLocations(data)
      setLocations(nextLocations)
      setInitialLocations(nextLocations)
      setClearedLocations({ volunteer: false, donation: false, visitor: false })
    } catch (_err) {
      setError(t('charities.settings.save_failed_retry'))
    } finally {
      setLoading(false)
    }
  }, [charityId, getCharity, getUserCharityRegistrations, t])

  useEffect(() => {
    fetchCharity()
  }, [fetchCharity])

  useEffect(() => {
    const stripeReturn = searchParams.get('stripe_return')
    const stripeRefresh = searchParams.get('stripe_refresh')

    if (stripeReturn === 'true') {
      setStripeMessage({
        type: 'success',
        text: t('charities.dashboard.stripe.messages.returnSuccess'),
      })
      searchParams.delete('stripe_return')
      searchParams.delete('charity_id')
      setSearchParams(searchParams, { replace: true })
    } else if (stripeRefresh === 'true') {
      setStripeMessage({
        type: 'warning',
        text: t('charities.dashboard.stripe.messages.sessionExpired'),
      })
      searchParams.delete('stripe_refresh')
      searchParams.delete('charity_id')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, t])

  const updateCharityField = useCallback((field, value) => {
    setCharity(prev => ({ ...prev, [field]: value }))
  }, [])

  const updateSocialField = useCallback((field, value) => {
    setCharity(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleLocationChange = useCallback((stateKey, place) => {
    const nextLocation = parseGooglePlaceToCharityLocation(place)
    setLocations(prev => ({ ...prev, [stateKey]: nextLocation }))
    setClearedLocations(prev => ({ ...prev, [stateKey]: nextLocation == null }))
  }, [])

  const applySavedCharity = useCallback(data => {
    setCharity(data)
    setInitialCharity(data)
    const nextLocations = buildInitialLocations(data)
    setLocations(nextLocations)
    setInitialLocations(nextLocations)
    setClearedLocations({
      volunteer: false,
      donation: false,
      visitor: false,
    })
  }, [])

  const imageCharityLogoOnChange = useCallback(
    async mediaAttachment => {
      if (!charityId || !mediaAttachment?.id) return

      setSaving(true)
      setError(null)

      try {
        const response = await updateCharity(charityId, {
          logo_media_attachment_id: mediaAttachment.id,
        })
        const status = response.status ?? response.error?.response?.status

        if (status === 403) {
          setAccessDenied(true)
          setError(t('charities.settings.access_denied'))
          return
        }

        if (response.errors?.length || (response.error && status !== 200)) {
          setError(
            response.errors?.full_messages?.[0] ||
              response.error?.response?.data?.error ||
              t('charities.settings.logo_upload_failed'),
          )
          return
        }

        if (response.data) {
          applySavedCharity(response.data)
        } else {
          const previewUrl =
            mediaAttachment.thumbnail_url ??
            mediaAttachment.direct_upload_url ??
            null
          setCharity(prev => ({
            ...prev,
            logo_url: previewUrl || prev?.logo_url,
          }))
          setInitialCharity(prev => ({
            ...prev,
            logo_url: previewUrl || prev?.logo_url,
          }))
        }
      } catch (_err) {
        setError(t('charities.settings.logo_upload_failed'))
      } finally {
        setSaving(false)
      }
    },
    [applySavedCharity, charityId, t, updateCharity],
  )

  const persistCharity = useCallback(
    async payload => {
      if (!charityId || !charity) return false

      setSaving(true)
      setError(null)

      try {
        const response = await updateCharity(charityId, payload)
        const status = response.status ?? response.error?.response?.status

        if (status === 403) {
          setAccessDenied(true)
          setError(t('charities.settings.access_denied'))
          return false
        }

        if (response.errors?.length) {
          setError(
            Array.isArray(response.errors)
              ? response.errors.join(', ')
              : String(response.errors),
          )
          return false
        }

        if (response.error && status !== 200) {
          setError(
            response.error?.response?.data?.error ||
              t('charities.settings.save_failed'),
          )
          return false
        }

        if (response.data) {
          applySavedCharity(response.data)
        }

        return true
      } catch (_err) {
        setError(t('charities.settings.save_failed_retry'))
        return false
      } finally {
        setSaving(false)
      }
    },
    [applySavedCharity, charity, charityId, t, updateCharity],
  )

  const onSaveDonationLinks = useCallback(async () => {
    const charityPayload = {}
    DONATION_LINK_FIELDS.forEach(field => {
      charityPayload[field] = normalizeLinkField(field, charity?.[field])
    })
    await persistCharity({ charity: charityPayload })
  }, [charity, persistCharity])

  const onSaveSocials = useCallback(async () => {
    const charityPayload = {}
    SOCIAL_LINK_FIELDS.forEach(field => {
      charityPayload[field] = normalizeLinkField(field, charity?.[field])
    })
    await persistCharity({ charity: charityPayload })
  }, [charity, persistCharity])

  const onSaveLocations = useCallback(async () => {
    const payload = {}

    CHARITY_LOCATION_TYPES.forEach(({ key, stateKey }) => {
      const current = locations[stateKey]
      const initial = initialLocations[stateKey]

      if (clearedLocations[stateKey] && initial) {
        payload[key] = null
        return
      }

      if (!charityLocationsEqual(current, initial)) {
        if (current) {
          payload[key] = charityLocationPayload(current)
        } else if (initial) {
          payload[key] = null
        }
      }
    })

    await persistCharity(payload)
  }, [clearedLocations, initialLocations, locations, persistCharity])

  const hasDonationLinksChanged = useMemo(
    () => fieldsChanged(charity, initialCharity, DONATION_LINK_FIELDS),
    [charity, initialCharity],
  )

  const hasSocialsChanged = useMemo(
    () => fieldsChanged(charity, initialCharity, SOCIAL_LINK_FIELDS),
    [charity, initialCharity],
  )

  const hasLocationsChanged = useMemo(() => {
    return CHARITY_LOCATION_TYPES.some(({ stateKey }) => {
      if (clearedLocations[stateKey] && initialLocations[stateKey]) return true
      return !charityLocationsEqual(
        locations[stateKey],
        initialLocations[stateKey],
      )
    })
  }, [clearedLocations, initialLocations, locations])

  const breadcrumbs = useMemo(() => {
    if (!charity) return []
    const profilePath = charityPublicProfilePath(charity.id)
    return [
      { label: t('navigation_banner.home'), to: '/' },
      {
        label:
          charity.organization_name || t('navigation_banner.charity_profile'),
        to: profilePath || undefined,
      },
      { label: t('charities.settings.title') },
    ]
  }, [charity, t])

  if (Number.isNaN(charityId)) {
    return <Navigate to={ROUTES.CHARITY_ACCOUNT} replace />
  }

  if (loading) {
    return <Loading show />
  }

  if (accessDenied) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">{t('charities.settings.access_denied')}</Alert>
        <Link to="/charities">{t('charities.settings.back_to_charities')}</Link>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="container mt-4">
        <Alert variant="warning">{t('charities.settings.not_found')}</Alert>
        <Link to="/charities">{t('charities.settings.back_to_charities')}</Link>
      </div>
    )
  }

  if (!charity) {
    return <Loading show />
  }

  return (
    <>
      {saving && <Loading show />}
      <CharitySettingsContent
        charity={charity}
        charityId={charityId}
        breadcrumbs={breadcrumbs}
        error={error}
        setError={setError}
        stripeMessage={stripeMessage}
        setStripeMessage={setStripeMessage}
        verifiedCharities={verifiedCharities}
        locations={locations}
        clearedLocations={clearedLocations}
        onLocationChange={handleLocationChange}
        updateCharityField={updateCharityField}
        updateSocialField={updateSocialField}
        onLogoChange={imageCharityLogoOnChange}
        onSaveDonationLinks={onSaveDonationLinks}
        onSaveSocials={onSaveSocials}
        onSaveLocations={onSaveLocations}
        hasDonationLinksChanged={hasDonationLinksChanged}
        hasSocialsChanged={hasSocialsChanged}
        hasLocationsChanged={hasLocationsChanged}
        saving={saving}
        stripeReturnPath={stripeReturnPath}
        t={t}
      />
    </>
  )
}
